import { useMemo, useState } from 'react';
import DepositEmailField, { depositEmailFor } from '@/components/deposits/DepositEmailField';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Clock,
  Palette,
  MapPin,
  History,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Phone,
  Instagram,
  Zap,
  FileText,
  Users,
  Layers,
  Image as ImageIcon,
  ArrowRight,
  HeartPulse,
  Plus,
  ShieldCheck,
  Radio,
  Armchair,
  XCircle,
  Banknote,
  Store,
} from 'lucide-react';
import clsx from 'clsx';
import type {
  Artist,
  Booking,
  ConfirmationStage,
  ConfirmationTouch,
  CustomerRecord,
  Deposit,
} from '@/domain/types';
import { aud, customerName, formatBodyPart, simTimeFull } from '@/utils/format';
import { bookingPriceAud } from '@/domain/pricing';
import { bookingMoney } from '@/domain/bookingMoney';
import { isCancellableNow, isPastBooking, pieceProgress } from '@atink/domain';
import { bookingStatusTone } from '@/utils/statusTone';
import { approvedByOpsLabel, approvedOverrideForBooking, pendingOverrideRequestForBooking } from '@/domain/approvals';
import {
  FLAG_STYLE,
  holdBriefText,
  REFS_LOCATION_LABEL,
  RESCHEDULE_REASON_LABEL,
} from '@/utils/bookingSummary';
import { computeBookingFlags } from '@/domain/bookingFlags';
import { payAtStudioDepositFor, payAtStudioStateFor, piecePotFor } from '@/domain/depositStatus';
import { payBookingDepositAtDesk } from '@/domain/depositFlow';
import { intentFailureCopy } from '@/api/intents';
import { checkInBooking, seatBooking } from '@/domain/sessionFlow';
import EditBookingModal from '@/components/EditBookingModal';
import WaiverModal from '@/components/WaiverModal';
import CustomerFlagChips from '@/components/CustomerFlagChips';
import { isForwardMove, useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { actorLabel } from '@/domain/actor';
import { canCancelBooking } from '@/domain/opsCancelFlow';
import { useSessionStore } from '@/state/sessionStore';
import CancelBookingPanel from '@/components/CancelBookingPanel';

interface Props {
  booking: Booking;
  customer: CustomerRecord | undefined;
  artist: Artist | undefined;
  deposit: Deposit | undefined;
  onClose: () => void;
  onViewCustomer: () => void;
  /** Jump the open card to a sibling booking (piece prev/next). */
  onNavigateBooking?: (bookingId: string) => void;
}

const CONFIRMATION_STAGE_LABEL: Record<ConfirmationStage, string> = {
  'pre-7d': 'Pre 7-day',
  '7d-text-sent': '7-day text sent',
  '7d-call-pending': '7-day call pending',
  '7d-call-done': '7-day call done',
  '2d-text-sent': '2-day text sent',
  confirmed: 'Confirmed',
  unreachable: 'Unreachable',
};

const TOUCH_CHANNEL_ICON = {
  sms: MessageSquare,
  call: Phone,
  ig: Instagram,
  'auto-studio': Zap,
} as const;

const TOUCH_OUTCOME_LABEL: Record<ConfirmationTouch['outcome'], string> = {
  confirmed: 'Confirmed',
  'no-response': 'No response',
  'left-vm': 'Left voicemail',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
};

function durationLabel(min: number): string {
  return min >= 60 ? `${(min / 60).toFixed(min % 60 ? 1 : 0)}h` : `${min}m`;
}

/**
 * Opened when a calendar tile is clicked (brief item 4). Surfaces the structured
 * brief decomposed from the old single notes box: flags, money + deposit ledger,
 * scheduled-vs-actual time, design + refs, piece continuity, the confirmation
 * cadence, reschedule history and invoice state.
 */
export default function BookingDetailCard({
  booking,
  customer,
  artist,
  deposit,
  onClose,
  onViewCustomer,
  onNavigateBooking,
}: Props) {
  const allBookings = useBrainStore((s) => s.bookings);
  const pieces = useBrainStore((s) => s.pieces);
  const allDeposits = useBrainStore((s) => s.deposits);
  const artistsMap = useBrainStore((s) => s.artists);
  const customersMap = useBrainStore((s) => s.customers);
  const waivers = useBrainStore((s) => s.waivers);
  const overrideRequests = useBrainStore((s) => s.overrideRequests);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const pushNotification = useUIStore((s) => s.pushNotification);

  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [medicalOpen, setMedicalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const persona = useSessionStore((s) => s.persona);
  const [payMenuOpen, setPayMenuOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  // D05-11: taking the deposit at the desk needs the client's email (on file, else typed).
  const [payEmail, setPayEmail] = useState('');

  const waiver = useMemo(
    () =>
      Object.values(waivers)
        .filter((w) => w.bookingId === booking.id)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0],
    [waivers, booking.id],
  );
  const waiverLabel =
    !waiver || waiver.status === 'not-sent'
      ? 'Send waiver'
      : waiver.status === 'signed'
        ? 'Waiver signed'
        : 'Waiver sent';

  // Live booking drives display + floor step actions so they react to a check-in /
  // seat done from inside this card, without the parent having to re-pass the
  // booking. Read every mutable field (status, timestamps, money, flags) off this,
  // not the stale `booking` prop.
  // calendar-11 (c): the passed booking (the live view) wins when it is further
  // along the day than the store's copy, so a check-in elsewhere never reads "Held".
  const stored = allBookings[booking.id];
  const liveBooking = stored && !isForwardMove(stored.status, booking.status) ? stored : booking;
  const liveStatus = liveBooking.status;
  // Cancel from the calendar (B9 calendar-14): the customer record's rule, per
  // booking — a live future booking cancels (the 7-day split), a past one that
  // was never attended is a no-show. Only Ops cancels; everyone else asks Ops.
  const cancelMode: 'cancel' | 'no-show' | null = isCancellableNow(liveBooking, clockIso)
    ? 'cancel'
    : (liveStatus === 'held' || liveStatus === 'pending-deposit' || liveStatus === 'confirmed') &&
        isPastBooking(liveBooking.start, clockIso)
      ? 'no-show'
      : null;
  const isOps = canCancelBooking(persona);
  // B4 (8tqu; Anas 1 Oct Q2): an Agent's "Pay at studio" — the client pays the
  // deposit at the desk. It stays a hold until then; the desk takes it from here.
  const payAtStudio = deposit ? payAtStudioDepositFor(liveBooking, { [deposit.id]: deposit }) : undefined;
  const payContactEmail = depositEmailFor(customersMap[liveBooking.customerId] ?? customer, payEmail);
  const takePayment = (method: 'eftpos' | 'cash') => {
    if (!payAtStudio || paying || !payContactEmail) return;
    setPaying(true);
    setPayMenuOpen(false);
    void payBookingDepositAtDesk({
      bookingId: liveBooking.id,
      amountAud: payAtStudio.amountAud,
      method,
      contactEmail: payContactEmail,
      onConfirmed: ({ amountAud }) =>
        pushNotification({
          intensity: 'high',
          title: 'Deposit taken at the desk',
          body: `${aud(amountAud)} by ${method === 'cash' ? 'cash' : 'EFTPOS'}. The booking is locked in.`,
          ttlMs: 5000,
        }),
      onFailed: (f) => {
        const copy = intentFailureCopy(f);
        pushNotification({ intensity: 'critical', title: copy.title, body: copy.body, ttlMs: 8000 });
      },
    }).finally(() => setPaying(false));
  };
  // No-deposit override state: a held booking with a pending request badges as
  // "awaiting approval"; an approved/direct waiver badges as "no deposit · waived".
  const pendingOverride = useMemo(
    () => pendingOverrideRequestForBooking(booking.id, overrideRequests),
    [overrideRequests, booking.id],
  );
  const depositWaived = liveBooking.depositWaived === true;
  // B3: who approved the waiver, for the "Approved by Ops" stamp.
  const approvedOverride = useMemo(
    () => (depositWaived ? approvedOverrideForBooking(booking.id, overrideRequests) : undefined),
    [depositWaived, overrideRequests, booking.id],
  );
  const waiverSigned = waiver?.status === 'signed';
  const custLabel = customer ? customerName(customer) : booking.customerId;

  // Step 1 — check in (hard-gated on the waiver by the domain function).
  const onCheckIn = () => {
    const result = checkInBooking(booking.id);
    if (!result.ok) {
      if (result.reason === 'waiver-unsigned') {
        pushNotification({
          intensity: 'high',
          title: 'Waiver not signed',
          body: `${custLabel} needs to sign the medical waiver before check-in.`,
          ttlMs: 4000,
        });
        setWaiverOpen(true);
      }
      return;
    }
    pushNotification({
      intensity: 'high',
      title: 'Checked in',
      body: `${custLabel} is checked in — seat them when a chair's ready.`,
      ttlMs: 4000,
    });
  };

  // Step 2 — seat in the chair (booth auto-assigned from this quick-action surface).
  const onSeat = () => {
    const result = seatBooking(booking.id);
    if (!result.ok) return;
    pushNotification({
      intensity: 'high',
      title: 'Seated · booth recording armed',
      body: `${custLabel} is in chair ${result.booking.boothNumber} for their session.`,
      ttlMs: 4000,
    });
  };

  const tone = bookingStatusTone(liveBooking.status);
  const history = liveBooking.rescheduleHistory ?? [];

  // Flags are derived, not stored: recomputed from the booking + related records
  // each render. Suggestions carry a dismiss affordance; derived facts don't.
  const flags = useMemo(
    () => computeBookingFlags(liveBooking, customer, deposit, Object.values(allBookings)),
    [liveBooking, customer, deposit, allBookings],
  );

  // Header cluster — loyalty + last-appt + primary artist, all derived from the
  // customer's booking history.
  const lastApptDays = useMemo(() => {
    const now = new Date(clockIso).getTime();
    let latest: number | null = null;
    for (const id of customer?.bookings ?? []) {
      if (id === booking.id) continue;
      const b = allBookings[id];
      if (!b) continue;
      const t = new Date(b.start).getTime();
      if (t <= now && (latest === null || t > latest)) latest = t;
    }
    if (latest === null) return null;
    return Math.max(0, Math.round((now - latest) / 86_400_000));
  }, [customer, allBookings, booking.id, clockIso]);

  const primaryArtist = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const id of customer?.bookings ?? []) {
      const b = allBookings[id];
      if (!b) continue;
      counts[b.artistId] = (counts[b.artistId] ?? 0) + 1;
    }
    let best: string | null = null;
    for (const [aid, n] of Object.entries(counts)) {
      if (best === null || n > (counts[best] ?? 0)) best = aid;
    }
    return best ? artistsMap[best] : undefined;
  }, [customer, allBookings, artistsMap]);

  // Money row.
  // ontheday-10: the same money answer as the Floor (price from the rate card when
  // there's no day rate; the deposit still free for this booking; balance >= 0).
  const money = bookingMoney(liveBooking, artist, deposit);
  const depositPaid = money.depositOnFileAud;
  const balanceDue = money.balanceAud;

  // Actual vs scheduled.
  const actualStartIso = liveBooking.actualStart ?? liveBooking.checkedInAt;
  const actualDuration =
    liveBooking.checkedInAt && liveBooking.checkedOutAt
      ? Math.max(
          0,
          Math.round(
            (new Date(liveBooking.checkedOutAt).getTime() -
              new Date(liveBooking.checkedInAt).getTime()) /
              60_000,
          ),
        )
      : null;

  // Design section gating — designBrief is legacy fallback only.
  const placementLabel = formatBodyPart(booking);
  const hasStructuredDesign = Boolean(
    booking.bodyPart ||
      booking.colour ||
      (booking.styleTags && booking.styleTags.length > 0) ||
      booking.refsLocation,
  );
  const showLegacyBrief = !booking.summary && !hasStructuredDesign && Boolean(booking.designBrief);

  // Refs chip scoping: the thumbnails are the source of truth. Show the location
  // chip only when refs live elsewhere and none are attached here; when nothing
  // is on file at all, offer to add some instead.
  const hasRefPhotos = booking.referencePhotos.length > 0;
  const hasExternalRefs = booking.refsLocation != null && booking.refsLocation !== 'uploaded';

  // References are edited through the booking's edit modal (ReferencePicker
  // lives there) — the card just opens it.
  const handleAddReferences = () => setEditOpen(true);

  // Piece continuity.
  const piece = booking.pieceId ? pieces[booking.pieceId] : undefined;
  const pieceSiblings = useMemo(() => {
    if (!piece) return [];
    return piece.bookingsLinked
      .map((id) => allBookings[id])
      .filter((b): b is Booking => Boolean(b))
      .sort((a, b) => (a.start < b.start ? -1 : 1));
  }, [piece, allBookings]);
  const pieceIndex = pieceSiblings.findIndex((b) => b.id === booking.id);
  const pieceTitle = piece ? piece.title || piece.style || piece.designType.replace(/-/g, ' ') : '';
  const prevSibling = pieceIndex > 0 ? pieceSiblings[pieceIndex - 1] : undefined;
  const nextSibling =
    pieceIndex >= 0 && pieceIndex < pieceSiblings.length - 1
      ? pieceSiblings[pieceIndex + 1]
      : undefined;

  // Confirmation cadence.
  const touches = useMemo(
    () => [...(booking.confirmationTouches ?? [])].sort((a, b) => (a.at < b.at ? -1 : 1)),
    [booking.confirmationTouches],
  );

  const collabArtists = (booking.collabArtistIds ?? [])
    .map((id) => artistsMap[id]?.displayName)
    .filter(Boolean) as string[];

  const splitNames = (deposit?.splitWithCustomerIds ?? [])
    .map((id) => {
      const c = customersMap[id];
      return c ? customerName(c) : undefined;
    })
    .filter(Boolean) as string[];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-center justify-center px-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 8, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-ink-deep border border-gold-dim/40 rounded-xl w-full max-w-3xl p-7 relative max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Booking detail"
          data-testid="booking-detail-card"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-cream-soft hover:text-cream"
            aria-label="Close"
          >
            <X size={16} />
          </button>

          {/* Header (full-width, above the two-column body) */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={clsx(
                'inline-block rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-ink-black',
                tone.dot,
              )}
              data-testid="booking-detail-status"
            >
              {tone.label}
            </span>
            {pendingOverride ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-orange px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest text-ink-black"
                data-testid="booking-awaiting-approval"
                title={`Requested by ${actorLabel(pendingOverride.requestedBy)}: "${pendingOverride.note}"`}
              >
                Awaiting no-deposit approval
              </span>
            ) : depositWaived && approvedOverride ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-mint px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest text-ink-black"
                data-testid="booking-deposit-approved"
                title={approvedOverride.note ? `Requested: "${approvedOverride.note}"` : undefined}
              >
                No deposit · {approvedByOpsLabel(approvedOverride)}
              </span>
            ) : depositWaived ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-mint px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest text-ink-black"
                data-testid="booking-deposit-waived"
              >
                No deposit · waived
              </span>
            ) : null}
            {payAtStudio ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-azure/20 px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest text-azure"
                data-testid="booking-pay-at-studio"
                title="The client pays the deposit at the desk. Held until then."
              >
                <Store size={11} /> Pay at studio · {aud(payAtStudio.amountAud)}
              </span>
            ) : null}
            {payAtStudio ? (
              <span className="relative">
                <button
                  type="button"
                  onClick={() => setPayMenuOpen((v) => !v)}
                  disabled={paying}
                  className="inline-flex items-center gap-1 rounded-full border border-mint/50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-mint hover:bg-mint/10 disabled:opacity-40"
                  data-testid="booking-take-payment"
                  aria-haspopup="menu"
                  aria-expanded={payMenuOpen}
                >
                  <Banknote size={11} /> {paying ? 'Taking payment…' : 'Take payment'}
                </button>
                {payMenuOpen ? (
                  <span className="absolute left-0 top-full z-20 mt-1 flex w-60 flex-col panel-dark py-1" role="menu">
                    <DepositEmailField
                      customer={customersMap[liveBooking.customerId] ?? customer}
                      value={payEmail}
                      onChange={setPayEmail}
                      className="px-3 py-1.5"
                    />
                    {(
                      [
                        { m: 'eftpos', label: 'Paid by EFTPOS' },
                        { m: 'cash', label: 'Paid by cash' },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.m}
                        type="button"
                        role="menuitem"
                        onClick={() => takePayment(opt.m)}
                        disabled={!payContactEmail}
                        title={payContactEmail ? undefined : "Add the client's email to take the deposit"}
                        className="px-3 py-2 text-left text-sm text-cream hover:bg-ink-soft/60 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`booking-take-payment-${opt.m}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex items-start justify-between gap-3">
            <h2 className="heading-display text-2xl text-cream">
              {customer ? customerName(customer) : (booking.notes ?? 'Booking')}
            </h2>
            {customer ? (
              <div
                className="flex flex-col items-end gap-1 shrink-0"
                data-testid="booking-detail-customer-cluster"
              >
                <span className="rounded-full border border-gold-dim/40 bg-gold/10 px-3 py-1 text-sm text-gold-bright">
                  {aud(customer.lifetimeSpendAud)} lifetime
                </span>
                {lastApptDays != null ? (
                  <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                    last appt {lastApptDays}d ago
                  </span>
                ) : null}
                {primaryArtist ? (
                  <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                    {primaryArtist.displayName}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Flags strip — derived facts (hover for detail). The medical-note
              pill is a toggle that expands the profile panel below. */}
          {flags.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="booking-detail-flags">
              {flags.map((flag) => {
                const style = FLAG_STYLE[flag.kind];
                const pillClass = clsx(
                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
                  style.cls,
                );
                if (flag.kind === 'medical-note') {
                  return (
                    <button
                      key={flag.kind}
                      type="button"
                      onClick={() => setMedicalOpen((v) => !v)}
                      title={flag.detail}
                      className={clsx(pillClass, 'cursor-pointer hover:brightness-125')}
                      aria-expanded={medicalOpen}
                      data-testid={`booking-detail-flag-${flag.kind}`}
                    >
                      {style.label}
                      <ChevronDown
                        size={11}
                        className={clsx('transition-transform', medicalOpen && 'rotate-180')}
                      />
                    </button>
                  );
                }
                return (
                  <span
                    key={flag.kind}
                    title={flag.detail}
                    className={clsx(pillClass, flag.detail && 'cursor-help')}
                    data-testid={`booking-detail-flag-${flag.kind}`}
                  >
                    {style.label}
                  </span>
                );
              })}
            </div>
          ) : null}

          {/* Derived customer-level flags (waiver signed / medical / allergy). */}
          {customer ? <CustomerFlagChips customer={customer} bookingId={booking.id} className="mt-2" /> : null}

          {/* Medical profile panel — expands below the flags strip, full width */}
          {medicalOpen && customer?.medicalProfile ? (
            <div
              className="mt-3 rounded-md border border-blood/40 bg-blood/5 p-3"
              data-testid="booking-detail-medical-panel"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 label-small-caps text-blood">
                  <HeartPulse size={13} /> Medical profile
                </span>
                <button
                  type="button"
                  onClick={onViewCustomer}
                  className="font-inter text-[10px] uppercase tracking-widest text-gold-bright hover:text-cream inline-flex items-center gap-1"
                  data-testid="booking-detail-medical-view-customer"
                >
                  View on customer record <ArrowRight size={11} />
                </button>
              </div>
              <dl className="mt-2 space-y-1.5 text-[12px]">
                {customer.medicalProfile.allergies?.length ? (
                  <MedicalPanelRow label="Allergies" values={customer.medicalProfile.allergies} />
                ) : null}
                {customer.medicalProfile.conditions?.length ? (
                  <MedicalPanelRow label="Conditions" values={customer.medicalProfile.conditions} />
                ) : null}
                {customer.medicalProfile.medications?.length ? (
                  <MedicalPanelRow label="Medications" values={customer.medicalProfile.medications} />
                ) : null}
              </dl>
              {customer.medicalProfile.notes ? (
                <p className="mt-2 font-serif text-[12px] text-cream-soft leading-relaxed">
                  {customer.medicalProfile.notes}
                </p>
              ) : null}
              <p className="mt-2 font-inter text-[10px] text-cream-faint uppercase tracking-wider">
                Updated {simTimeFull(customer.medicalProfile.lastUpdatedAt)} ·{' '}
                {customer.medicalProfile.lastUpdatedBy}
              </p>
            </div>
          ) : null}

          {/* Two-column body. Left = money / time / design / piece; right =
              collab / confirmation / history. Collapses to a single column
              below md, preserving the left-then-right stacking order. */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
            {/* Left column */}
            <div className="min-w-0 space-y-4" data-testid="booking-detail-col-left">
              {/* Money row */}
              <div
                className="grid grid-cols-3 gap-2 rounded-md border border-gold-dim/30 p-3 text-center"
                data-testid="booking-detail-money"
              >
                <div>
                  <p className="label-small-caps text-cream-faint">Price</p>
                  <p className="mt-1 text-sm text-cream" data-testid="booking-detail-price">
                    {aud(money.priceAud)}
                  </p>
                </div>
                <div className="border-x border-gold-dim/20">
                  <p className="label-small-caps text-cream-faint">Deposit on file</p>
                  <p
                    className={clsx('mt-1 text-sm', depositPaid > 0 ? 'text-mint' : 'text-cream-soft')}
                    data-testid="booking-detail-deposit-on-file"
                  >
                    {aud(depositPaid)}
                  </p>
                </div>
                <div>
                  <p className="label-small-caps text-cream-faint">Balance due</p>
                  <p className="mt-1 text-sm text-gold-bright" data-testid="booking-detail-balance">
                    {aud(balanceDue)}
                  </p>
                </div>
              </div>

              {/* Deposit ledger (collapsible) */}
              {deposit && deposit.ledger && deposit.ledger.length > 0 ? (
                <div data-testid="booking-detail-ledger">
                  <button
                    type="button"
                    onClick={() => setLedgerOpen((v) => !v)}
                    className="flex w-full items-center justify-between text-cream-soft hover:text-cream"
                    aria-expanded={ledgerOpen}
                    data-testid="booking-detail-ledger-toggle"
                  >
                    <span className="label-small-caps">Deposit ledger ({deposit.ledger.length})</span>
                    <ChevronDown
                      size={14}
                      className={clsx('transition-transform', ledgerOpen && 'rotate-180')}
                    />
                  </button>
                  {ledgerOpen ? (
                    <ul className="mt-2 space-y-1">
                      {deposit.ledger.map((entry, i) => {
                        const outbound = entry.kind === 'rollover-out' || entry.kind === 'forfeit' || entry.kind === 'transfer-out' || entry.kind === 'refunded';
                        return (
                          <li
                            key={`${entry.at}-${i}`}
                            className="flex items-center justify-between font-mono text-[10px] text-cream-soft"
                          >
                            <span className="text-cream-faint">{simTimeFull(entry.at)}</span>
                            <span className="flex items-center gap-1.5">
                              <span className={outbound ? 'text-blood' : 'text-mint'}>
                                {outbound ? '−' : '+'}
                                {aud(entry.amountAud)}
                              </span>
                              <span className="capitalize">{entry.kind.replace(/-/g, ' ')}</span>
                              {entry.by ? <span className="text-cream-faint">· {actorLabel(entry.by)}</span> : null}
                            </span>
                          </li>
                        );
                      })}
                      {splitNames.length > 0 ? (
                        <li className="pt-1 font-mono text-[10px] text-cream-faint">
                          Split with: {splitNames.join(', ')}
                        </li>
                      ) : null}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              {/* Time row */}
              <dl className="space-y-2.5 text-sm">
                <div className="flex items-center gap-2 text-cream">
                  <Clock size={13} className="text-gold-dim shrink-0" />
                  <span>
                    {simTimeFull(booking.start)} · {durationLabel(booking.durationMin)}
                  </span>
                </div>
                {actualStartIso ? (
                  <div className="flex items-center gap-2 text-cream-soft">
                    <Clock size={13} className="text-mint shrink-0" />
                    <span>
                      Actual: {simTimeFull(actualStartIso)}
                      {actualDuration != null ? ` · ${durationLabel(actualDuration)}` : ' · in progress'}
                    </span>
                  </div>
                ) : null}
                {artist ? (
                  <div className="flex items-center gap-2 text-cream">
                    <User size={13} className="text-gold-dim shrink-0" />
                    <span>
                      {artist.displayName} · {aud(bookingPriceAud(booking, artist))}
                    </span>
                  </div>
                ) : null}
              </dl>

              {/* Design section */}
              <div>
                <p className="label-small-caps text-gold-bright">Design</p>
                <dl className="mt-2 space-y-2.5 text-sm">
                  {placementLabel ? (
                    <div className="flex items-center gap-2 text-cream">
                      <MapPin size={13} className="text-gold-dim shrink-0" />
                      <span>{placementLabel}</span>
                    </div>
                  ) : null}
                  {booking.colour ? (
                    <div className="flex items-center gap-2 text-cream">
                      <Palette size={13} className="text-gold-dim shrink-0" />
                      <span>{booking.colour === 'colour' ? 'Colour' : 'Black & grey'}</span>
                    </div>
                  ) : null}
                  {!hasRefPhotos && hasExternalRefs ? (
                    <div className="flex items-center gap-2">
                      <ImageIcon size={13} className="text-orange shrink-0" />
                      <span
                        className="inline-flex items-center rounded-full border border-orange/50 bg-orange/15 px-2.5 py-0.5 text-[11px] text-orange"
                        data-testid="booking-detail-refs-chip"
                      >
                        Refs: {REFS_LOCATION_LABEL[booking.refsLocation!]}
                      </span>
                    </div>
                  ) : !hasRefPhotos ? (
                    <button
                      type="button"
                      onClick={handleAddReferences}
                      className="inline-flex items-center gap-1 rounded-md border border-gold-dim/40 px-2.5 py-1 text-[11px] text-cream-soft hover:border-gold-bright/60 hover:text-cream transition"
                      data-testid="booking-detail-add-refs"
                    >
                      <Plus size={12} /> Add references
                    </button>
                  ) : null}
                </dl>

                {booking.styleTags && booking.styleTags.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="booking-detail-tags">
                    {booking.styleTags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-gold-dim/40 px-2.5 py-0.5 text-[11px] capitalize text-cream-soft"
                      >
                        {tag.replace(/-/g, ' ')}
                      </span>
                    ))}
                  </div>
                ) : null}

                {booking.referencePhotos.length > 0 ? (
                  <div className="mt-3 flex gap-1.5" data-testid="booking-detail-thumbs">
                    {booking.referencePhotos.slice(0, 4).map((url, i) => (
                      <img
                        key={`${url}-${i}`}
                        src={url}
                        alt=""
                        loading="lazy"
                        className="h-12 w-12 object-cover rounded-sm border border-gold-dim/20"
                      />
                    ))}
                  </div>
                ) : null}

                {booking.summary ? (
                  <p className="mt-3 font-serif text-[13px] text-cream-soft" data-testid="booking-detail-summary">
                    {booking.summary}
                  </p>
                ) : showLegacyBrief ? (
                  <p className="mt-3 font-serif text-[13px] text-cream-soft" data-testid="booking-detail-brief">
                    {holdBriefText(booking.designBrief, deposit ? payAtStudioStateFor(liveBooking, { [deposit.id]: deposit }) : undefined, liveBooking.status)}
                  </p>
                ) : (
                  // TODO: build the summary from the structured design fields (rule-built, no AI).
                  <button
                    type="button"
                    disabled
                    className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-gold-dim/30 px-2.5 py-1 text-[11px] text-cream-faint opacity-50 cursor-not-allowed"
                    data-testid="booking-detail-generate-summary"
                  >
                    <FileText size={12} /> Generate summary
                  </button>
                )}
              </div>

              {/* Piece continuity */}
              {piece ? (
                <div
                  className="rounded-md border border-gold-dim/30 px-3 py-2"
                  data-testid="booking-detail-piece"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-sm text-cream">
                      <Layers size={13} className="text-gold-dim shrink-0" />
                      {/* THE x-of-y (@atink/domain pieceProgress) — same as the Floor row. */}
                      Session {pieceProgress(booking, piece).label} ·{' '}
                      <span className="capitalize">{pieceTitle}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={!prevSibling || !onNavigateBooking}
                        onClick={() => prevSibling && onNavigateBooking?.(prevSibling.id)}
                        className="text-cream-soft hover:text-gold-bright disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Previous session"
                        data-testid="booking-detail-piece-prev"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        disabled={!nextSibling || !onNavigateBooking}
                        onClick={() => nextSibling && onNavigateBooking?.(nextSibling.id)}
                        className="text-cream-soft hover:text-gold-bright disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Next session"
                        data-testid="booking-detail-piece-next"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </span>
                  </div>
                  {/* 75mu: one deposit pot on the piece, across its sessions. */}
                  <p className="mt-1 font-inter text-[11px] text-cream-soft" data-testid="booking-detail-piece-pot">
                    Deposit pot on this piece · {aud(piecePotFor(piece.id, allDeposits, allBookings))}
                  </p>
                </div>
              ) : null}
            </div>

            {/* Right column */}
            <div className="min-w-0 space-y-4" data-testid="booking-detail-col-right">
              {/* Collab */}
              {collabArtists.length > 0 ? (
                <p
                  className="flex items-center gap-1.5 text-[12px] text-cream-soft"
                  data-testid="booking-detail-collab"
                >
                  <Users size={12} className="text-gold-dim" />
                  Collab with: {collabArtists.join(', ')}
                </p>
              ) : null}

              {/* Confirmation timeline */}
              {booking.confirmationStage || touches.length > 0 ? (
                <div data-testid="booking-detail-confirmation">
                  <div className="flex items-center gap-2">
                    <span className="label-small-caps text-cream-soft">Confirmation</span>
                    {booking.confirmationStage ? (
                      <span className="rounded-full border border-gold-dim/40 px-2 py-0.5 text-[10px] text-gold-bright">
                        {CONFIRMATION_STAGE_LABEL[booking.confirmationStage]}
                      </span>
                    ) : null}
                  </div>
                  {touches.length > 0 ? (
                    <ul className="mt-2 space-y-1">
                      {touches.map((touch, i) => {
                        const Icon = TOUCH_CHANNEL_ICON[touch.channel];
                        return (
                          <li
                            key={`${touch.at}-${i}`}
                            className="flex items-center gap-2 font-mono text-[10px] text-cream-soft"
                          >
                            <span className="text-cream-faint">{simTimeFull(touch.at)}</span>
                            <Icon size={11} className="text-gold-dim shrink-0" />
                            <span>{TOUCH_OUTCOME_LABEL[touch.outcome]}</span>
                            <span className="text-cream-faint">· {actorLabel(touch.by)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              {/* Reschedule history */}
              {history.length > 0 ? (
                <div data-testid="booking-detail-history">
                  <div className="flex items-center gap-1.5">
                    <History size={12} className="text-gold-dim" />
                    <span className="label-small-caps text-cream-soft">Reschedule history</span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {history.map((h, i) => (
                      <li key={i} className="font-mono text-[10px] text-cream-soft">
                        {simTimeFull(h.fromIso)} → {simTimeFull(h.toIso)}
                        <span className="text-cream-faint"> · by {actorLabel(h.by)}</span>
                        {h.reason ? (
                          <span className="text-cream-faint"> · {RESCHEDULE_REASON_LABEL[h.reason]}</span>
                        ) : null}
                        {h.note ? <span className="text-cream-faint"> · &ldquo;{h.note}&rdquo;</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-2 mt-5 justify-end">
            {customer ? (
              <button
                type="button"
                onClick={() => setWaiverOpen(true)}
                className={clsx(
                  'btn-ghost text-xs mr-auto',
                  waiver?.status === 'signed' && 'text-mint',
                )}
                data-testid="booking-detail-waiver"
                title="Medical waiver"
              >
                <ShieldCheck size={12} /> {waiverLabel}
              </button>
            ) : null}
            {liveStatus === 'confirmed' ? (
              <button
                type="button"
                onClick={onCheckIn}
                className="btn-primary text-xs"
                data-testid="booking-detail-check-in"
                title={
                  waiverSigned
                    ? 'Check in at the desk'
                    : 'Check in — prompts to sign the waiver first'
                }
              >
                <Radio size={12} /> Check in
              </button>
            ) : liveStatus === 'checked-in' ? (
              <button
                type="button"
                onClick={onSeat}
                className="btn-primary text-xs"
                data-testid="booking-detail-seat"
              >
                <Armchair size={12} /> Place in chair
              </button>
            ) : null}
            {cancelMode ? (
              <button
                type="button"
                onClick={() => setCancelOpen(true)}
                className="btn-ghost text-xs text-blood"
                data-testid={isOps ? 'booking-detail-cancel' : 'booking-detail-ask-ops-cancel'}
              >
                <XCircle size={12} />{' '}
                {!isOps ? 'Ask Ops to cancel' : cancelMode === 'no-show' ? 'Mark no-show' : 'Cancel'}
              </button>
            ) : null}
            <button type="button" onClick={onClose} className="btn-ghost text-xs">
              Close
            </button>
            <button
              type="button"
              onClick={onViewCustomer}
              className={clsx(
                'text-xs',
                // Keep a single primary: the step action owns it on the floor,
                // otherwise "View customer" stays the primary CTA.
                liveStatus === 'confirmed' || liveStatus === 'checked-in'
                  ? 'btn-ghost'
                  : 'btn-primary',
              )}
              data-testid="booking-detail-view-customer"
            >
              View customer
            </button>
          </div>

          <EditBookingModal
            open={editOpen}
            bookingId={booking.id}
            onClose={() => setEditOpen(false)}
          />
          <WaiverModal open={waiverOpen} booking={booking} onClose={() => setWaiverOpen(false)} />
          {cancelOpen && cancelMode ? (
            <div
              className="fixed inset-0 z-[60] bg-overlay/80 flex items-center justify-center px-6"
              onClick={(e) => {
                e.stopPropagation();
                setCancelOpen(false);
              }}
            >
              <div
                className="panel w-full max-w-md p-6"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label={isOps ? 'Cancel booking' : 'Ask Ops to cancel'}
                data-testid="booking-detail-cancel-panel"
              >
                <p className="label-small-caps text-blood">
                  <XCircle size={12} className="inline mr-2" />
                  {!isOps ? 'Ask Ops to cancel' : cancelMode === 'no-show' ? 'Mark no-show' : 'Cancel booking'}
                </p>
                <h2 className="heading-display text-xl text-cream mt-2">
                  {customer ? customerName(customer) : 'Booking'} · {simTimeFull(liveBooking.start)}
                </h2>
                <CancelBookingPanel
                  booking={liveBooking}
                  mode={cancelMode}
                  source="calendar"
                  onBack={() => setCancelOpen(false)}
                  onDone={() => {
                    setCancelOpen(false);
                    onClose();
                  }}
                />
              </div>
            </div>
          ) : null}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function MedicalPanelRow({ label, values }: { label: string; values: string[] }) {
  return (
    <div className="flex items-start gap-2">
      <dt className="font-inter text-[10px] text-cream-faint uppercase tracking-wider shrink-0 pt-0.5">
        {label}
      </dt>
      <dd className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <span
            key={v}
            className="rounded-full border border-blood/50 bg-blood/15 px-2 py-0.5 text-[11px] capitalize text-blood"
          >
            {v}
          </span>
        ))}
      </dd>
    </div>
  );
}
